import type {CopilotSpaceResource, CopilotSpaceRepositoryBackedResource} from '../types'
import type {OwnerItem} from '../components/SpacesOwnerDropdown'

interface ResourcesForDuplicationResult {
  resourcesToSave: CopilotSpaceResource[]
  resourcesThatWillNotTransfer: CopilotSpaceRepositoryBackedResource[]
}

/**
 * Determines which resources can be saved when duplicating a space to a new owner
 * and which will not transfer due to access restrictions.
 *
 * Private resources owned by a different organization/user than the target owner
 * will not be transferable if the target owner is an Organization.
 *
 * If the target owner is an individual user, all resources can be transferred.
 *
 * @param resources - The resources from the original space
 * @param targetOwner - The new owner for the duplicated space
 * @returns An object containing resources that can be saved and those that cannot transfer
 */
export function resourcesForDuplication(
  resources: CopilotSpaceResource[],
  targetOwner: OwnerItem,
): ResourcesForDuplicationResult {
  const resourcesToSave: CopilotSpaceResource[] = []
  const resourcesThatWillNotTransfer: CopilotSpaceRepositoryBackedResource[] = []

  if (targetOwner.type === 'Organization' && !targetOwner.allowsPrivateReposFromOtherOrgs) {
    // If the target owner is an organization that does NOT allow private repos from other orgs, we need to check for organization-level access
    for (const resource of resources) {
      // Check if this is a repository-backed resource with ownership and privacy info
      if ('ownerId' in resource && 'private' in resource && resource.private && resource.ownerId !== targetOwner.id) {
        // Private resource owned by different owner - cannot transfer
        resourcesThatWillNotTransfer.push(resource)
      } else {
        // Resource can be saved
        resourcesToSave.push(resource)
      }
    }

    return {
      resourcesToSave,
      resourcesThatWillNotTransfer,
    }
  } else {
    // If the target owner is a user then they can save all resources, even if the resource is org-owned and private.
    // This is because the resources in the space are already filtered to what the current user can see, so we can safely
    // assume duplicating the current user will preserve access to all resources.
    return {
      resourcesToSave: resources,
      resourcesThatWillNotTransfer: [],
    }
  }
}
